import { useEffect, useState } from 'react';
import { useUserData } from '../auth';
import { supabase } from '../supabase';
import { useWarehouseSettings } from './useWarehouseSettings';
import { LEGACY_WAREHOUSE_LINE_UNITS, buildUnitOptions, matchUnit, withCurrentUnit } from '../constants/units';

/**
 * Unità di misura predefinita dell'organizzazione (es. Say Farm: grammi 'g').
 *
 * REGOLA: il comportamento nuovo si attiva SOLO se l'org ha impostato
 * `settings.warehouse_settings.default_unit_of_measure` (Impostazioni →
 * Magazzino → Prodotti → Classificazione e unità di misura). Senza impostazione ogni funzione restituisce
 * esattamente il valore/elenco che il form usava prima (il `fallback` o il
 * `legacyOptions` passati dal chiamante), così le altre organizzazioni non
 * vedono differenze.
 *
 * Con l'impostazione attiva:
 * - una riga/articolo nuovo parte dall'unità predefinita invece che dal
 *   valore fisso del form ('pz', 'kg', ...);
 * - scegliendo un articolo si usa la sua UDM 1 (products.unit_of_measure_1),
 *   e solo se manca l'unità predefinita — l'unità dell'articolo ha la
 *   precedenza, così un articolo a pezzi non diventa "1 g". La UDM 1
 *   dell'articolo vale anche per le org SENZA impostazione (vedi unitForProduct);
 * - i menu a tendina mostrano le unità configurate dall'org (tabella
 *   `units_of_measure`) invece della lista fissa del form.
 */

// Cached separately for each organization, including in-flight requests.
const unitsCache = new Map<string,string[]>();
const unitsInflight = new Map<string,Promise<string[]>>();
const listeners = new Set<()=>void>();
let generation = 0;
async function loadOrgUnits(org: string): Promise<string[]> {
  if (unitsCache.has(org)) return unitsCache.get(org)!;
  if (!unitsInflight.has(org)) {
    const started = generation;
    const request = (async () => {
      const all: string[] = [];
      for(let offset=0;;offset+=500) {
        const {data,error} = await supabase.from('units_of_measure').select('name,id').eq('organization_id',org).order('name').order('id').range(offset,offset+499);
        if(error) throw error;
        all.push(...(data || []).map(u=>u.name).filter(Boolean));
        if((data || []).length<500) break;
      }
      if(started===generation) unitsCache.set(org,all);
      return all;
    })();
    unitsInflight.set(org,request);
    void request.then(()=>{if(unitsInflight.get(org)===request) unitsInflight.delete(org);},()=>{if(unitsInflight.get(org)===request) unitsInflight.delete(org);});
  }
  return unitsInflight.get(org)!;
}
export function invalidateOrgUnitsOfMeasure(): void {
  generation++; unitsCache.clear(); unitsInflight.clear(); listeners.forEach(f=>f());
}
export function useOrgUnitOfMeasure() {
  const { organizationId } = useUserData();
  const { default_unit_of_measure: orgDefault, loaded } = useWarehouseSettings();
  const [result,setResult] = useState<{org:string;units:string[]} | null>(null);
  const [revision,setRevision] = useState(0);
  useEffect(()=>{ const refresh=()=>{setResult(null);setRevision(n=>n+1);}; listeners.add(refresh);return()=>{listeners.delete(refresh);}; },[]);
  useEffect(() => {
    let cancelled=false;
    if(organizationId && orgDefault) loadOrgUnits(organizationId).then(units=>{if(!cancelled)setResult({org:organizationId,units});}).catch(()=>{if(!cancelled)setResult({org:organizationId,units:[]});});
    return()=>{cancelled=true;};
  }, [organizationId,orgDefault,revision]);
  const orgUnits = organizationId && orgDefault ? (result?.org===organizationId ? result.units : unitsCache.get(organizationId) || []) : [];

  const active = !!orgDefault;

  /** Unità di una riga/articolo nuovo: predefinita dell'org, altrimenti quella di sempre del form */
  const unitForNew = (fallback: string): string => orgDefault || fallback;

  /**
   * Unità da usare quando si sceglie un articolo: UDM 1 dell'articolo, poi
   * l'unità predefinita dell'org, poi `current`. La UDM 1 vale per tutte le
   * org: il carico/scarico di flusia-core (stockQuantity) rifiuta una riga in
   * un'unità diversa da quella di magazzino dell'articolo senza conversione.
   */
  const unitForProduct = (productUnit: string | null | undefined, current: string): string => {
    const unit = productUnit?.trim();
    // «pezzi» dell'anagrafica diventa la voce equivalente dei menu («pz»): una sola unità per i pezzi
    if (unit) return matchUnit(unit, active ? buildUnitOptions(orgDefault, orgUnits) : LEGACY_WAREHOUSE_LINE_UNITS) ?? unit;
    return orgDefault || current;
  };

  /**
   * Opzioni per il menu unità. Senza impostazione: l'elenco fisso del form,
   * invariato. Con impostazione: unità dell'org, con la predefinita e il valore
   * corrente sempre presenti.
   */
  const unitOptions = (current: string | null | undefined, legacyOptions: readonly string[]): string[] => {
    // Il valore corrente (es. la UDM 1 dell'articolo) resta sempre selezionabile
    if (!active) return withCurrentUnit(current, legacyOptions);
    const withDefault = buildUnitOptions(orgDefault, orgUnits);
    return buildUnitOptions(current, withDefault);
  };

  return { orgDefault, active, loaded, orgUnits, unitForNew, unitForProduct, unitOptions };
}
